import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CategoryIcon } from './CategoryIcon';
import { categoryLabel, formatDistance, isOpenNow, promoRemaining } from '../lib/mapConfig';

function WhatsAppGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// Card rico do estabelecimento exibido no popup do mapa (também usado como
// detalhe do estabelecimento selecionado). Mantém os textos/aria que os e2e
// esperam ("⚡ Destaque", "🎁 Promoção", "WhatsApp", "Rota", "Renovar promoção").
export function BusinessPopupCard({
  business,
  promo = null,
  promoLoading = false,
  canManagePromo = false,
  hours = null,
  favorited = false,
  favoriteLoading = false,
  onToggleFavorite = null,
}) {
  const navigate = useNavigate();
  const boosted = Boolean(business.isBoosted);
  const hasPromo = Boolean(business.hasPromotion || promo);
  const open = isOpenNow(hours);
  const distance = formatDistance(business.distance);
  const remaining = promo ? promoRemaining(promo.valid_until || promo.validUntil) : null;

  const waHref = business.whatsapp ? `https://wa.me/${business.whatsapp}` : null;
  const routeHref = `https://www.google.com/maps/dir/?api=1&destination=${business.lat},${business.lng}`;

  const showPromoSection = boosted || hasPromo;

  return (
    <div className="nc-map-card">
      <div className="nc-map-card__head">
        <div className="nc-map-card__logo">
          {business.logo_url ? (
            <img src={business.logo_url} alt={`Logo de ${business.name}`} loading="lazy" />
          ) : (
            <CategoryIcon category={business.category} size={22} />
          )}
        </div>
        <div className="nc-map-card__title">
          <div className="nc-map-card__name-row">
            <span className="nc-map-card__name">{business.name}</span>
            {boosted && <span className="nc-map-card__badge nc-map-card__badge--boost">⚡ Destaque</span>}
            {!boosted && hasPromo && <span className="nc-map-card__badge">Promo</span>}
          </div>
          <div className="nc-map-card__address">{business.address}</div>
        </div>
        {onToggleFavorite && (
          <button
            type="button"
            className={`nc-map-card__fav${favorited ? ' nc-map-card__fav--active' : ''}`}
            onClick={onToggleFavorite}
            disabled={favoriteLoading}
            aria-label={favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
            aria-pressed={favorited}
            title={favorited ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill={favorited ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>
        )}
      </div>

      <div className="nc-map-card__meta">
        <span className="nc-map-card__chip">
          <CategoryIcon category={business.category} size={13} strokeWidth={2} />
          {categoryLabel(business.category)}
        </span>
        {distance && <span className="nc-map-card__chip nc-map-card__chip--muted">{distance}</span>}
        {open != null && (
          <span className={`nc-map-card__status${open ? ' nc-map-card__status--open' : ''}`}>
            <span className="nc-map-card__status-dot" aria-hidden="true" />
            {open ? 'Aberto agora' : 'Fechado'}
          </span>
        )}
      </div>

      {showPromoSection && (
        <div className="nc-map-card__promo">
          {promoLoading ? (
            <div className="nc-map-card__promo-loading" role="status" aria-live="polite">
              Carregando promoção...
            </div>
          ) : promo ? (
            <>
              <div className="nc-map-card__promo-title">🎁 {promo.title}</div>
              {promo.description && (
                <div className="nc-map-card__promo-desc">{promo.description}</div>
              )}
              <div className="nc-map-card__promo-foot">
                {promo.valid_until && (
                  <span className="nc-map-card__promo-until">
                    Válido até {String(promo.valid_until).slice(0, 10)}
                  </span>
                )}
                {remaining && (
                  <span className={remaining.ended ? 'nc-map-card__promo-ended' : ''}>
                    {remaining.label}
                  </span>
                )}
              </div>
              {canManagePromo && (
                <button
                  type="button"
                  className="nc-map-card__renew"
                  aria-label="Renovar promoção"
                  onClick={() => navigate(`/comerciante/publicacoes?business_id=${business.id}&renew=${promo.id}`)}
                >
                  🔄 Renovar promoção
                </button>
              )}
            </>
          ) : (
            <div className="nc-map-card__promo-empty">Sem promoção ativa no momento.</div>
          )}
        </div>
      )}

      <div className="nc-map-card__actions">
        <button
          type="button"
          className={`nc-map-card__action nc-map-card__action--primary${boosted ? ' nc-map-card__action--boost' : ''}`}
          onClick={() => navigate(`/comercio/${business.id}`)}
        >
          {boosted ? '🎁 Promoção' : 'Ver detalhes'}
        </button>
        {waHref && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="nc-map-card__action nc-map-card__action--whatsapp"
            aria-label="WhatsApp"
            title="Chamar no WhatsApp"
          >
            <WhatsAppGlyph />
          </a>
        )}
        <a
          href={routeHref}
          target="_blank"
          rel="noopener noreferrer"
          className="nc-map-card__action nc-map-card__action--route"
          title="Como chegar"
        >
          Rota
        </a>
      </div>
    </div>
  );
}
